/**
 * Le carnet du local — chantier 7, étape 3 (maquettes 20 et 21, validées le 24/09).
 *
 * UN carnet par local. En haut, la FRISE des passages (entrées / sorties). Au milieu,
 * l'ÉTAT ACTUEL par pièce — ce que verra le prochain occupant — avec, pour chaque point,
 * sa fiche et l'avancement de ses travaux. « Faire l'entrée / la sortie » ouvre la saisie
 * PIÈCE PAR PIÈCE, pensée pour le téléphone ; un point dégradé peut ouvrir sa fiche
 * directement. Quand la fiche est résolue, le point revient seul à « Bon » (serveur).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException, type CarnetDTO, type PointCarnet } from '@/lib/api';

const ETATS: Array<[PointCarnet['etat'], string, string]> = [
  ['ok', 'Bon', 'bg-success/15 text-success'], ['a_surveiller', 'À surveiller', 'bg-warning/15 text-warning'], ['degrade', 'Dégradé', 'bg-urgent/15 text-urgent'],
];
const lib = (e: PointCarnet['etat']) => ETATS.find((x) => x[0] === e)!;
const STATUT_FICHE: Record<string, string> = { recu: 'Reçue', planifie: 'Planifiée', en_cours: 'Travaux en cours', resolu: 'Réparée', archive: 'Archivée' };
const piecesDe = <T extends { piece?: string | null }>(pts: T[]) => {
  const m = new Map<string, T[]>();
  for (const p of pts) { const k = p.piece?.trim() || 'Général'; m.set(k, [...(m.get(k) ?? []), p]); }
  return [...m.entries()];
};
const frDate = (iso: string) => iso.split('-').reverse().join('/');

export function CarnetDuLocal({ lieuId }: { lieuId: string }) {
  const [c, setC] = useState<CarnetDTO | null>(null);
  const [sens, setSens] = useState<'entree' | 'sortie' | null>(null);
  const [msg, setMsg] = useState<{ ok: boolean; t: string } | null>(null);
  const charger = useCallback(() => api.admin.lieux.carnet(lieuId).then(setC).catch(() => setC(null)), [lieuId]);
  useEffect(() => { void charger(); }, [charger]);

  async function declarer(pointId: string) {
    if (!c?.edl) return;
    setMsg(null);
    try { const f = await api.admin.lieux.declarerFiche(c.edl.id, pointId, {}); setMsg({ ok: true, t: `Fiche ${f.numero} ouverte.` }); await charger(); }
    catch (e) { setMsg({ ok: false, t: e instanceof ApiException ? e.payload.error : 'Déclaration impossible' }); }
  }

  if (!c) return <p className="text-sm text-ink-muted">Chargement du carnet…</p>;
  if (!c.edl) return (
    <Card variant="glass" padded><p className="text-sm text-ink">Ce bâtiment n’a pas encore de carnet.</p>
      <p className="mt-1 text-xs text-ink-muted">Créez son premier état des lieux depuis son modèle (liste ci-dessous) : il deviendra le carnet du local.</p></Card>
  );
  return (
    <div className="space-y-4">
      {msg && <p className={`text-xs ${msg.ok ? 'text-success' : 'text-urgent'}`} role={msg.ok ? 'status' : 'alert'}>{msg.t}</p>}
      <Card variant="glass" padded>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <h2 className="flex-1 text-sm font-semibold text-ink">Les passages <span className="font-normal text-ink-muted">· carnet {c.edl.numero}</span></h2>
          <Button size="sm" variant="glass" onClick={() => setSens('sortie')}>Faire la sortie</Button>
          <Button size="sm" variant="primary" onClick={() => setSens('entree')}>Faire l’entrée</Button>
        </div>
        {c.passages.length === 0 ? <p className="text-xs text-ink-subtle">Aucun passage enregistré : le prochain état des lieux ouvrira la frise.</p> : (
          <ol aria-label="Passages, du plus récent au plus ancien" className="flex gap-2 overflow-x-auto pb-1">
            {c.passages.map((p) => (
              <li key={p.id} className="min-w-[180px] rounded-r-lg border border-border/60 bg-surface/40 px-3 py-2">
                <p className="text-2xs"><span className={`rounded-r-pill px-2 py-0.5 font-medium ${p.sens === 'entree' ? 'bg-success/15 text-success' : 'bg-tech/15 text-tech-fg'}`}>{p.sens === 'entree' ? 'Entrée' : 'Sortie'}</span> <span className="text-ink-subtle">{frDate(p.datePassage)}</span></p>
                <p className="mt-1 text-sm font-medium text-ink">{p.occupant}</p>
                <p className="text-2xs text-ink-muted">{p.degrades ? `${p.degrades} dégradé(s)` : 'Rien de dégradé'}{p.aSurveiller ? ` · ${p.aSurveiller} à surveiller` : ''}</p>
              </li>
            ))}
          </ol>
        )}
      </Card>

      <Card variant="glass" className="overflow-hidden p-0">
        <div className="flex items-center border-b border-border px-4 py-3"><h2 className="flex-1 text-sm font-semibold text-ink">État actuel du local</h2><span className="text-2xs text-ink-muted">ce que verra le prochain occupant</span></div>
        {piecesDe(c.points).map(([piece, pts]) => (
          <div key={piece}>
            <p className="bg-surface-hi/40 px-4 py-1.5 text-2xs font-semibold uppercase tracking-wider text-ink-subtle">{piece}</p>
            {pts.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 border-t border-border/50 px-4 py-2.5 text-sm">
                <span className="min-w-[160px] flex-1 text-ink">{p.libelle}{p.note && <span className="block text-2xs text-ink-muted">{p.note}</span>}</span>
                <span className={`rounded-r-pill px-2 py-0.5 text-2xs font-medium ${lib(p.etat)[2]}`}>{lib(p.etat)[1]}</span>
                <span className="min-w-[200px] text-xs">
                  {p.fiche ? <><Link className="text-accent underline" to={`/service-technique/signalements/${p.fiche.id}`}>{p.fiche.numero}</Link>{' '}
                    <span className="text-ink-muted">{p.fiche.etapeCircuit ?? STATUT_FICHE[p.fiche.statut] ?? p.fiche.statut}</span></>
                    : p.etat === 'degrade' ? <Button size="sm" variant="ghost" onClick={() => void declarer(p.id)}>Déclarer une fiche</Button> : null}
                </span>
              </div>
            ))}
          </div>
        ))}
      </Card>
      {sens && <PassageDialog sens={sens} edlId={c.edl.id} points={c.points} onClose={() => setSens(null)}
        onFini={(t) => { setSens(null); setMsg({ ok: true, t }); void charger(); }} />}
    </div>
  );
}

/** La saisie d'un passage, pièce par pièce (téléphone du service technique). */
function PassageDialog({ sens, edlId, points, onClose, onFini }: {
  sens: 'entree' | 'sortie'; edlId: string; points: PointCarnet[]; onClose: () => void; onFini: (t: string) => void;
}) {
  const [occupant, setOccupant] = useState('');
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [pts, setPts] = useState<PointCarnet[]>(() => points.map(({ id, libelle, etat, note, coutEstime, piece, ficheId, repareLe }) => ({ id, libelle, etat, note: note ?? '', coutEstime, piece, ficheId, repareLe })));
  const [ficher, setFicher] = useState<Set<string>>(new Set());
  const [err, setErr] = useState<string | null>(null);
  const pieces = useMemo(() => piecesDe(pts).map(([k]) => k), [pts]);
  const [i, setI] = useState(0);
  const piece = pieces[i] ?? 'Général';
  const maj = (id: string, patch: Partial<PointCarnet>) => setPts((l) => l.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  async function enregistrer() {
    setErr(null);
    try {
      await api.admin.lieux.enregistrerPassage(edlId, { sens, occupant: occupant.trim(), datePassage: date, checklist: pts });
      let n = 0;
      for (const id of ficher) { const p = pts.find((x) => x.id === id); if (p?.etat === 'degrade' && !p.ficheId) { await api.admin.lieux.declarerFiche(edlId, id, { note: p.note }); n += 1; } }
      onFini(`${sens === 'entree' ? 'Entrée' : 'Sortie'} enregistrée${n ? ` · ${n} fiche(s) ouverte(s)` : ''}.`);
    } catch (e) { setErr(e instanceof ApiException ? e.payload.error : 'Enregistrement impossible'); }
  }

  return (
    <Dialog open onClose={onClose} title={sens === 'entree' ? 'État des lieux d’entrée' : 'État des lieux de sortie'} size="lg"
      footer={<>
        <Button variant="ghost" onClick={onClose}>Annuler</Button>
        {i < pieces.length - 1
          ? <Button variant="primary" onClick={() => setI(i + 1)}>Pièce suivante →</Button>
          : <Button variant="primary" disabled={occupant.trim().length < 2} onClick={() => void enregistrer()}>Enregistrer {sens === 'entree' ? 'l’entrée' : 'la sortie'}</Button>}
      </>}>
      <div className="space-y-3">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr_160px]">
          <label className="text-xs text-ink">Occupant<input className="mt-1 h-11 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={occupant} maxLength={200}
            placeholder="Ex : Club de football" onChange={(e) => setOccupant(e.target.value)} /></label>
          <label className="text-xs text-ink">Date<input type="date" className="mt-1 h-11 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={date} onChange={(e) => setDate(e.target.value)} /></label>
        </div>
        <div role="tablist" aria-label="Pièces" className="flex flex-wrap gap-1.5">
          {pieces.map((k, n) => (
            <button key={k} type="button" role="tab" aria-selected={n === i} onClick={() => setI(n)}
              className={`h-8 rounded-r-pill border px-3 text-xs ${n === i ? 'border-tech bg-tech/15 font-medium text-ink' : 'border-border text-ink-muted'}`}>{k}</button>
          ))}
        </div>
        {pts.filter((p) => (p.piece?.trim() || 'Général') === piece).map((p) => (
          <div key={p.id} className="border-t border-border/60 pt-3">
            <p className="mb-2 text-sm text-ink">{p.libelle}{p.ficheId && <span className="ml-2 text-2xs text-ink-muted">(fiche en cours)</span>}</p>
            <div role="radiogroup" aria-label={`État : ${p.libelle}`} className="grid grid-cols-3 gap-1 rounded-r-lg border border-border p-1">
              {ETATS.map(([e, l, cls]) => (
                <button key={e} type="button" role="radio" aria-checked={p.etat === e} onClick={() => maj(p.id, { etat: e })}
                  className={`h-11 rounded-r-md text-sm font-medium ${p.etat === e ? cls : 'text-ink-muted'}`}>{l}</button>
              ))}
            </div>
            {p.etat !== 'ok' && (
              <div className="mt-2 space-y-2">
                <input className="h-10 w-full rounded-r-md border border-border bg-surface px-2 text-sm" value={p.note ?? ''} maxLength={1000}
                  placeholder="Ce qui est constaté" aria-label={`Constat : ${p.libelle}`} onChange={(e) => maj(p.id, { note: e.target.value })} />
                {p.etat === 'degrade' && !p.ficheId && (
                  <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={ficher.has(p.id)}
                    onChange={(e) => setFicher((s) => { const n = new Set(s); if (e.target.checked) n.add(p.id); else n.delete(p.id); return n; })} />
                    Déclarer une fiche pour ce point</label>
                )}
              </div>
            )}
          </div>
        ))}
        {err && <p className="text-xs text-urgent" role="alert">{err}</p>}
      </div>
    </Dialog>
  );
}
